import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { RUM_IGNORED_ERROR_MESSAGES } from 'src/utils/error';
import { RUM_VERSION } from 'src/utils/rumVersion';

import clientEnv from '@savvy/env/client';

const SERVICE = 'savvy-pinetree-app';

type RumCredentials = {
  readonly env: string;
  readonly rumApplicationId: string;
  readonly rumClientToken: string;
};

type LogsCredentials = {
  readonly env: string;
  readonly logsClientToken: string;
};

export type ObservabilityUser = {
  readonly id: string | undefined;
  readonly name: string;
  readonly email: string | undefined;
  readonly role: string | undefined;
  readonly isInternal: boolean;
  readonly isLoginAsSession: boolean;
};

/**
 * RUM collects uncaught errors through its own instrumentation, so the app's
 * `window.onerror` returning true does not suppress them. `beforeSend` is the
 * only seam that reaches every error source (`source`, `console`, `custom`).
 *
 * Returning false discards the event.
 */
export function discardIgnoredRumEvents(event: RumEvent): boolean {
  if (event.type !== 'error') {
    return true;
  }
  const message = event.error.message;
  return !RUM_IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored));
}

// Annotated as RumInitConfiguration so excess-property checking rejects renamed
// keys. The previous config lived in a template string, which is how it kept
// passing the v4 names `trackInteractions` and `trackFrustrations` to a v6
// bundle that recognizes neither.
export function buildRumConfig({ env, rumApplicationId, rumClientToken }: RumCredentials): RumInitConfiguration {
  return {
    applicationId: rumApplicationId,
    clientToken: rumClientToken,
    site: 'datadoghq.com',
    service: SERVICE,
    env,
    version: RUM_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Inject trace-context headers into first-party API calls so RUM sessions
    // link to the backend APM trace on the Rails service.
    allowedTracingUrls: [{ match: 'https://api.savvywealth.com', propagatorTypes: ['datadog', 'tracecontext'] }],
    // Sample 100% of browser-initiated backend traces for full RUM->APM
    // correlation. Independent of RUM session sampling.
    traceSampleRate: 100,
    beforeSend: discardIgnoredRumEvents,
  };
}

export function buildLogsConfig({ env, logsClientToken }: LogsCredentials): LogsInitConfiguration {
  return {
    clientToken: logsClientToken,
    site: 'datadoghq.com',
    service: SERVICE,
    env,
    version: RUM_VERSION,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  };
}

// `forceRUM` is the only way to exercise this on a preview deploy: the app is
// only instrumented in production, so a config change cannot be QA'd anywhere else.
export function isObservabilityEnabled(search: string): boolean {
  return clientEnv.NEXT_PUBLIC_VERCEL_ENV === 'production' || new URLSearchParams(search).has('forceRUM');
}

let rumStarted = false;
let logsStarted = false;

/**
 * Starts RUM and Logs once per page load. Safe to call from both routers:
 * whichever runs first wins and the other is a no-op. Each SDK is gated on its
 * own credentials, since RUM does not need the Logs token.
 */
export function initObservability(): void {
  if (typeof window === 'undefined' || !isObservabilityEnabled(window.location.search)) {
    return;
  }

  const env = clientEnv.NEXT_PUBLIC_VERCEL_ENV;
  const rumApplicationId = process.env['NEXT_PUBLIC_DATADOG_RUM_APP_ID'];
  const rumClientToken = process.env['NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN'];
  const logsClientToken = process.env['NEXT_PUBLIC_DATADOG_CLIENT_KEY'];

  if (!rumStarted && rumApplicationId && rumClientToken) {
    rumStarted = true;
    datadogRum.init(buildRumConfig({ env, rumApplicationId, rumClientToken }));
  }

  if (!logsStarted && logsClientToken) {
    logsStarted = true;
    datadogLogs.init(buildLogsConfig({ env, logsClientToken }));
  }
}

/**
 * Tags the session with who it belongs to. Internal traffic is tagged rather
 * than dropped so the raw data stays intact and the exclusion lives in the
 * dashboard query instead of a hardcoded email list (DES2-123).
 *
 * Logs get only the id and the internal flag, under the attribute names the v4
 * `addLoggerGlobalContext` calls wrote: forwarded errors must not carry a name
 * or email, and existing log queries match on `@user_id`.
 */
export function identifyObservabilityUser(user: ObservabilityUser): void {
  if (rumStarted) {
    datadogRum.setUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      is_internal_user: user.isInternal,
      login_as_session: user.isLoginAsSession,
    });
  }

  if (logsStarted) {
    datadogLogs.setGlobalContextProperty('user_id', user.id);
    datadogLogs.setGlobalContextProperty('is_internal_user', user.isInternal);
  }
}
